<template>
    <div v-if="loading" class="flex min-h-80 items-center justify-center"><Loader /></div>
    <div v-else class="p-4">
        <div class="ds-employee-form-section">
            <div class="ds-employee-form-section-divider mb-2">
                <div class="ds-employee-form-section-title">Основные данные</div>
            </div>
            <div class="ds-employee-form-data-grid mb-4">
                <div class="ds-employee-form-data-column">
                    <base-input v-model="lastName" max-length="20" @input="validation()">Фамилия</base-input>
                    <base-input v-model="firstName" max-length="20" @input="validation()">Имя</base-input>
                    <base-input v-model="middleName" max-length="20">Отчество</base-input>
                </div>

                <div class="ds-employee-form-data-column">
                    <phone-input v-model="phone" @input="validation()">Телефон</phone-input>
                    <base-input v-model="email" max-length="50" @input="validation()">E-mail</base-input>
                </div>
            </div>

            <div class="ds-employee-form-section-divider mb-2">
                <div class="ds-employee-form-section-title">Трудоустройство</div>
            </div>
            <EmployeeEmploymentFields
                v-model:employee-tabel-code="tabcode"
                v-model:department="department"
                v-model:position="position"
                v-model:hired-date="date"
                v-model:fired-date="firedDate"
                v-model:percentage="percentage"
                v-model:shift-schedule="shiftSchedule"
                :departments="departmentStore.myDepartments"
                :positions="scheduleStore.positions"
                :shift-schedules="scheduleStore.shiftSchedules"
                :label-length="54"
                @change="validation"
            />
        </div>

        <div class="pt-3 text-right">
            <btn @click="save" :disabled="isDisabled" :class="{ 'opacity-50': isDisabled }">Сохранить</btn>
            <btn class="ml-3" type="secondary" @click="cancel">Отмена</btn>
        </div>
    </div>
</template>

<script setup lang="ts">

interface User {
    first_name: string,
    last_name: string,
    middle_name: string,
    phone: string,
    email?: string
}
import { ref, watch } from 'vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useEmploymentStore } from '@/stores/employmentStore'
import type { Option } from '@/types'
import dayjs from 'dayjs'
import Loader from '@/ui/Loader.vue'
import EmployeeEmploymentFields from './EmployeeEmploymentFields.vue'
import { formatEmploymentDateForApi } from '@/utils/employmentDate'

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const employmentStore = useEmploymentStore()
const emit = defineEmits(['close', 'created'])

const loading = ref(false)
const firstName = ref('')
const lastName = ref('')
const middleName = ref('')
const phone = ref('')
const email = ref('')
const tabcode = ref('')
const status = ref('')
const isDisabled = ref(true)
const position = ref<Option>({id: '', name: 'Выбрать должность'})
const department = ref<Option>(departmentStore.myDepartments[0])
const date = ref(dayjs())
const firedDate = ref()
const percentage = ref('100')
const shiftSchedule = ref<Option>(scheduleStore.shiftSchedules[0])

const validation = () => {
    const re = /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
    const hasValidPercentage = /^\d+$/.test(percentage.value) && Number(percentage.value) >= 0 && Number(percentage.value) <= 100

    isDisabled.value = !(
        firstName.value.length > 1 &&
        lastName.value.length > 1 &&
        phone.value.length > 6 &&
        position.value.id &&
        department.value?.id &&
        shiftSchedule.value?.id &&
        hasValidPercentage &&
        (!email.value || re.test(email.value)) &&
        (!firedDate.value || dayjs(date.value).isBefore(dayjs(firedDate.value)))
    )
}

const resetFields = () => {
    firstName.value = ''
    lastName.value = ''
    middleName.value = ''
    phone.value = ''
    email.value = ''
    tabcode.value = ''
    status.value = ''
    position.value = scheduleStore.positions[0]
    department.value = departmentStore.myDepartments[0]
    date.value = dayjs()
    firedDate.value = ''
    percentage.value = '100'
    shiftSchedule.value = scheduleStore.shiftSchedules[0]
}

const cancel = () => {
    emit('close')
}

const save = async () => {
    loading.value = true
    const user: User = {
            first_name: firstName.value,
            last_name: lastName.value,
            middle_name: middleName.value,
            phone: phone.value,
            
        }
    if(email.value) user.email = email.value

    const newUser = await employmentStore.addUser(user)
    if(newUser.id) {
        let fired = formatEmploymentDateForApi(firedDate.value)
        const data: Record<string, string | number | null> = {
            department: department.value.id,
            position: position.value.id,
            percentage: +percentage.value/100,
            shift_schedule: shiftSchedule.value.id,
            hired_at: formatEmploymentDateForApi(date.value),
            fired_at: fired,
            user: newUser.id
        }
        if(tabcode.value) data.employee_tabel_code = tabcode.value
        
        const res = await employmentStore.createEmployment(data)
        if(res.id) {
            resetFields()
        }
    }
    
    loading.value = false
    emit('created')
}

watch([date, firedDate, percentage, position, department, shiftSchedule], () => {validation()})

</script>
